import { MEAL_TYPES } from "../../../common/Meal_Types";
import { DEFAULT_MEAL_IMAGE } from "../model/constants";
import {
  NutritionDailyType,
  PlanMealItemCommonType,
  PlanMealRootItemType,
  PlanMealType,
} from "../model/nutritionPlanTypes";
import { timeToMinutes } from "./planDates";

/**
 * Pure read helpers lifted out of the mobile `NutritionStore` (the `NS*`
 * getters). They take plain plan data and return plain values, so the
 * provider, the components and the tests can share them without a store.
 */

export type MealTypeTab = typeof MEAL_TYPES[number];

export type ResolvedMealItem = {
  mealItem: PlanMealItemCommonType;
  isExtra: boolean;
  isAlternative: boolean;
  /** An alternative picked by a nutritionist (`auto === false`), not by the system. */
  isNutritionistApproved: boolean;
};

export const isFoodItem = (item: PlanMealItemCommonType): boolean =>
  item.food !== undefined && item.food !== null;

export const getTitle = (item: PlanMealItemCommonType): string =>
  isFoodItem(item) ? item.food!.name : item.recipe?.name ?? "";

export const getPhotoUrl = (item: PlanMealItemCommonType): string => {
  const url = isFoodItem(item) ? item.food!.photo_url : item.recipe?.photo_url;
  return url || DEFAULT_MEAL_IMAGE;
};

/** Calories rounded to a whole number, as the cards display them. */
export const getCalories = (item: PlanMealItemCommonType): number => {
  const cals = isFoodItem(item) ? item.food!.cals : item.recipe?.cals;
  return Math.round(cals ?? 0);
};

export const isLiked = (item: PlanMealItemCommonType): boolean =>
  isFoodItem(item)
    ? item.food!.is_liked === true
    : item.recipe?.is_liked === true;

/**
 * The item a root slot actually shows: the selected alternative when the user
 * (or a nutritionist) swapped it, otherwise the root item. Extras never have
 * alternatives. Mirrors the mobile `NSHelperGetAppropriateMealItem`.
 */
export const resolveMealItem = (
  item: PlanMealRootItemType
): ResolvedMealItem => {
  if (!item.extra) {
    const selected = item.alternatives?.find((alt) => alt.selected === true);
    if (selected) {
      return {
        mealItem: selected,
        isExtra: false,
        isAlternative: true,
        isNutritionistApproved: selected.auto === false,
      };
    }
  }
  return {
    mealItem: item,
    isExtra: item.extra === true,
    isAlternative: false,
    isNutritionistApproved: false,
  };
};

/**
 * Meal-type tabs for a day, ordered by `time_of_consumption`. Meal types the
 * lookup table does not know are dropped.
 */
export const calculateAvailableMealTypes = (
  meals: PlanMealType[] | undefined
): MealTypeTab[] => {
  if (!meals || meals.length === 0) return [];
  return [...meals]
    .sort(
      (a, b) =>
        timeToMinutes(a.time_of_consumption) -
        timeToMinutes(b.time_of_consumption)
    )
    .map((meal) => MEAL_TYPES.find((entry) => entry.type === meal.type))
    .filter((entry): entry is MealTypeTab => entry !== undefined);
};

export const findMealByType = (
  day: NutritionDailyType | undefined,
  type: number
): PlanMealType | undefined => day?.meals?.find((meal) => meal.type === type);

/** A meal counts as logged when any of its items is logged (mobile parity). */
export const isMealLogged = (meal: PlanMealType | undefined): boolean =>
  !!meal?.items?.length && meal.items.some((item) => item.is_logged);
